/* eslint-disable eqeqeq */
/* eslint-disable indent */
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Card, Row, Col } from 'react-bootstrap'
import axios from 'axios'
import { skills as modeToSkillMap } from '../services/arrays'
import { getTranslation } from '../services/translations'
import {
  calculateActualAge,
  prepareTrainingPlanData,
} from '../services/helpfunctions'
import { getU21Badge } from '../services/u21utils'
import PlayerCardHeader from './PlayerCardHeader'
import PlayerInfoCard from './PlayerInfoCard'
import PlayerSkillsCard from './PlayerSkillsCard'
import TrainingParamsCard from './TrainingParamsCard'
import TrainButton from './TrainButton'
import ScoutCommentsCard from './ScoutCommentsCard'
import TargetSkillsCard from './TargetSkillsCard'
import SkillUpdateHistory from './SkillUpdateHistory'

const ScoutSkills = ({
  player,
  playerSkills,
  setPlayerSkills,
  calculateSkillChange,
  message,
  addMessage,
  candidates,
  trainingPlan,
  setTrainingPlan,
  exportedTargetSkills,
  exportedOptimizedWeeks,
  nation,
  ownerNation,
}) => {
  const navigate = useNavigate()
  const user = useSelector(state => state.user)
  const lang = (user && user.language) || 'fi'
  const t = key => getTranslation(lang, key)
  const [selectedCandidate, setSelectedCandidate] = useState('')
  const [targetSkills, setTargetSkills] = useState([])
  const [selectedOptimizePosition, setSelectedOptimizePosition] = useState('CD')
  const [scoutComments, setScoutComments] = useState(player.scoutComments || '')
  const [savedScoutComments, setSavedScoutComments] = useState(
    player.scoutComments || ''
  )
  const [scoutCommentAuthor, setScoutCommentAuthor] = useState(
    player.scoutCommentAuthor || ''
  )
  const [scoutCommentUpdatedAt, setScoutCommentUpdatedAt] = useState(
    player.scoutCommentUpdatedAt || null
  )
  const [isSavingScoutComment, setIsSavingScoutComment] = useState(false)
  const [skillUpdateHistory, setSkillUpdateHistory] = useState(
    player.skillUpdateHistory || []
  )
  const userToken = user ? user.oauth_token || user.id : null
  const playerIdentifier = player.playerId || player.id
  const language = lang === 'en' ? 1 : 0

  // Laske harjoitushistoriasta toteutuneet viikot per taito suunnitelman jälkeen
  const getCompletedWeeksPerSkill = () => {
    if (!trainingPlan || !trainingPlan.date || !trainingPlan.skillWeeks)
      return {}
    const history = player.trainhistory || []
    const planDate = new Date(trainingPlan.date)
    const counts = {}
    history.forEach(entry => {
      if (!entry || !entry.date) return
      const entryDate = new Date(entry.date)
      if (entryDate > planDate) {
        const entryMode = Number(entry.mode)
        const entrySkill = modeToSkillMap[entryMode - 2]
        if (entrySkill) {
          counts[entrySkill] = (counts[entrySkill] || 0) + 1
        }
      }
    })
    return counts
  }

  const completedWeeks = getCompletedWeeksPerSkill()

  // Pelipaikkojen lista optimointia varten
  const optimizePositions = ['GK', 'CD', 'WBN', 'WTM', 'IMN', 'IMD', 'FW']

  // Käsittele palautetut tavoitetaidot SkillDistribution-sivulta
  const [shouldAutoSave, setShouldAutoSave] = React.useState(false)

  React.useEffect(() => {
    if (
      exportedTargetSkills &&
      exportedTargetSkills.length > 0 &&
      exportedOptimizedWeeks
    ) {
      // Suodata taidot joilla on treeniviikat > 0
      const validSkills = exportedTargetSkills.filter((s, index) => {
        const weeks = exportedOptimizedWeeks[index]
        // Hyväksy vain jos viikkomäärä on numero ja suurempi kuin 0
        return (
          weeks !== undefined &&
          weeks !== '-' &&
          weeks !== null &&
          Number(weeks) > 0
        )
      })

      if (validSkills.length > 0) {
        // Nollaa aiempi harjoitussuunnitelma ennen uusien taitojen asettamista
        setTrainingPlan(null)

        // Aseta palautetut tavoitetaidot
        setTargetSkills(
          validSkills.map(s => ({
            skill: s.skill,
            target: s.target.toString(),
          }))
        )
        addMessage(t('optimizedSkillsImported') || 'Optimoidut taidot tuotu')

        // Merkitse että automaattinen tallennus pitää tehdä
        setShouldAutoSave(true)
      }
    }
  }, [exportedTargetSkills, exportedOptimizedWeeks])

  // Automaattinen tallennus kun targetSkills on päivitetty
  React.useEffect(() => {
    if (shouldAutoSave && targetSkills.length > 0) {
      // Pieni viive varmistaa että kaikki on valmista
      const timer = setTimeout(() => {
        handleSubmitTrainingPlan()
        setShouldAutoSave(false)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [shouldAutoSave, targetSkills])

  const handleSkillChange = e => {
    const { name, value } = e.target
    //console.log(value)
    setPlayerSkills(prevSkills => ({
      ...prevSkills,
      //[name]: Number(value.replace(',', '.')),
      [name]: value,
    }))
  }

  // Alusta targetSkills pelaajan scoutTrainingPlanista
  // HUOM: Ei alusteta jos exportedTargetSkills on käytettävissä (tuotu optimoinnista)
  React.useEffect(() => {
    // Älä alusta jos optimoidut taidot on jo tuotu
    if (exportedTargetSkills && exportedTargetSkills.length > 0) {
      return
    }

    if (
      player &&
      player.scoutTrainingPlan &&
      Array.isArray(player.scoutTrainingPlan.finalLevels) &&
      player.scoutTrainingPlan.finalLevels.length > 0 &&
      targetSkills.length === 0
    ) {
      const initialTargets = player.scoutTrainingPlan.finalLevels.map(level => {
        const raw =
          level.finalLevel !== undefined && level.finalLevel !== null
            ? level.finalLevel
            : level.target
        const formatted = Number.isFinite(raw)
          ? raw.toFixed(2)
          : (raw !== undefined && raw !== null ? raw : '').toString()
        return {
          skill: level.skill,
          target: formatted,
        }
      })
      setTargetSkills(initialTargets)
    }
  }, [player, targetSkills.length, exportedTargetSkills])

  //console.log(trainingPlan)

  const updatePlayer = async () => {
    try {
      if (player.isCustom) {
        const response = await axios.put(
          '/api/players/custom/' + player.id,
          {
            playerSkills: {
              ...playerSkills,
            },
          },
          {
            headers: {
              'x-oauth-token': userToken,
            },
          }
        )
        if (response?.data?.playerSkills) {
          setPlayerSkills(response.data.playerSkills)
        }
        setSkillUpdateHistory(response.data.skillUpdateHistory || [])
      } else {
        const updatedPlayer = {
          ...player,
          playerSkills: {
            ...playerSkills,
          },
        }
        const response = await axios.put(
          '/api/players/override/' + player.playerId,
          { HattrickData: { Player: updatedPlayer } },
          { headers: { 'x-oauth-token': userToken } }
        )
        setPlayerSkills(response.data.playerSkills || playerSkills)
        setSkillUpdateHistory(response.data.skillUpdateHistory || [])
      }
      addMessage(t('playerSkillsUpdated'))
    } catch (error) {
      console.error('Player update failed:', error)
      addMessage(t('updateFailed'))
    }
  }

  const updateScoutComments = async newComments => {
    try {
      setIsSavingScoutComment(true)
      if (player.isCustom) {
        const response = await axios.put(
          '/api/players/custom/' + player.id,
          {
            scoutComments: newComments,
          },
          {
            headers: {
              'x-oauth-token': userToken,
            },
          }
        )
        setScoutCommentAuthor(response.data.scoutCommentAuthor || '')
        setScoutCommentUpdatedAt(response.data.scoutCommentUpdatedAt || null)
      } else {
        const scoutCommentAuthor = newComments.trim()
          ? user?.loginName || user?.LoginName || ''
          : ''
        const scoutCommentUpdatedAt = new Date().toISOString()
        const response = await axios.put(
          '/api/players/override/' + player.playerId,
          {
            scoutCommentSave: true,
            HattrickData: {
              Player: {
                ...player,
                scoutComments: newComments,
                scoutCommentAuthor,
                scoutCommentUpdatedAt,
              },
            },
          },
          { headers: { 'x-oauth-token': userToken } }
        )
        setScoutCommentAuthor(response.data.scoutCommentAuthor || '')
        setScoutCommentUpdatedAt(response.data.scoutCommentUpdatedAt || null)
      }
      setSavedScoutComments(newComments)
      addMessage(t('scoutCommentsUpdated') || 'Kommentit päivitetty')
    } catch (error) {
      console.error('Scout comments update failed:', error)
      addMessage(t('updateFailed'))
    } finally {
      setIsSavingScoutComment(false)
    }
  }

  const handleScoutCommentsChange = e => {
    const newValue = e.target.value
    setScoutComments(newValue)
  }

  const handleCandidateSelect = e => {
    setSelectedCandidate(e.target.value)
  }

  const updateCandidate = async () => {
    if (!player.playerId || player.isCustom) {
      addMessage('Luotuja pelaajia ei voi lisätä listoille')
      return
    }

    try {
      await axios.put(`/api/candidates/${selectedCandidate}`, {
        playerId: player.playerId,
      })
      addMessage(`${t('playerAddedToList')}`)
    } catch (error) {
      addMessage(t('addingFailed'))
    }
  }

  // Funktio, joka lisää uuden tavoitetaidon listalle
  const handleAddTargetSkill = () => {
    setTargetSkills([...targetSkills, { skill: '', target: '' }])
  }

  // Funktio, joka käsittelee tavoitteen muutoksen
  const handleTargetSkillChange = (index, field, value) => {
    const newTargetSkills = [...targetSkills]
    newTargetSkills[index][field] = value
    setTargetSkills(newTargetSkills)
  }

  const handleDeleteTrainingPlan = async () => {
    try {
      await axios.get('/api/players/deletemytrainingplan/' + player.playerId)
      setTrainingPlan()
      setTargetSkills([])
      addMessage(t('trainingPlanDeleted'))
    } catch (error) {
      addMessage(t('trainingPlanDeleteFailed'))
    }
  }

  // Funktio navigoi SkillDistribution sivulle pelaajan tiedoilla
  const handleOptimizeSkills = () => {
    if (!playerIdentifier) {
      addMessage('Pelaajan tunniste puuttuu')
      return
    }

    // Välitetään pelaajan tiedot navigaation state:ssa ja käytetään URL-parametria
    navigate(`/skilldistribution/${playerIdentifier}`, {
      state: {
        optimizePlayer: {
          playerId: playerIdentifier,
          firstName: player.firstName,
          lastName: player.lastName,
          age: player.age,
          ageDays: player.ageDays,
          playerSkills: playerSkills,
          position: selectedOptimizePosition,
        },
      },
    })
  }

  // Funktio lähettää taidot backendille
  const handleSubmitTrainingPlan = async () => {
    try {
      const trainingData = prepareTrainingPlanData(
        player,
        playerSkills,
        targetSkills
      )

      const response = await axios.post(
        '/api/players/mytrainingplan',
        trainingData
      )
      setTrainingPlan(response.data)
      addMessage(t('trainingPlanSent'))
    } catch (error) {
      addMessage(t('trainingPlanSendFailed'))
    }
  }

  const actualAge = calculateActualAge(player)
  const u21Badge =
    actualAge.years <= 21 ? getU21Badge(actualAge.years, actualAge.days) : null

  return (
    <>
      <Row className="d-flex align-items-start">
        {/* Vasen sarake - Pääkortti + Listat */}
        <Col lg={9} className="d-flex flex-column" style={{ gap: '12px' }}>
          {/* Pääkortti: Header + perustiedot/taidot/harjoitusparametrit */}
          <Card
            style={{
              backgroundColor: '#2C2F33',
              color: '#FFFFFF',
              borderColor: '#1E2124',
            }}
          >
            <PlayerCardHeader
              player={player}
              nation={nation}
              ownerNation={ownerNation}
              actualAge={actualAge}
              u21Badge={u21Badge}
              t={t}
              candidates={candidates}
              message={message}
            />
            <Card.Body style={{ padding: '10px' }}>
              <Row className="g-2">
                <Col md={6} xl={4} className="d-flex">
                  <PlayerInfoCard
                    player={player}
                    playerSkills={playerSkills}
                    lang={lang}
                  />
                </Col>
                <Col md={6} xl={4} className="d-flex">
                  <PlayerSkillsCard
                    playerSkills={playerSkills}
                    oldPlayerSkills={player.oldPlayerSkills}
                    calculateSkillChange={calculateSkillChange}
                    handleSkillChange={handleSkillChange}
                    updatePlayer={updatePlayer}
                    language={language}
                    lang={lang}
                  />
                </Col>
                <Col md={6} xl={4} className="d-flex">
                  <TrainingParamsCard
                    player={player}
                    language={language}
                    lang={lang}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Harjoittelulaskuri */}
          <TrainButton player={player} lang={lang} />
        </Col>

        {/* Oikea sarake - Tavoitetaidot + Kommentit */}
        <Col lg={3} className="d-flex flex-column" style={{ gap: '12px' }}>
          <TargetSkillsCard
            player={player}
            targetSkills={targetSkills}
            handleTargetSkillChange={handleTargetSkillChange}
            handleAddTargetSkill={handleAddTargetSkill}
            handleSubmitTrainingPlan={handleSubmitTrainingPlan}
            handleDeleteTrainingPlan={handleDeleteTrainingPlan}
            trainingPlan={trainingPlan}
            completedWeeks={completedWeeks}
            selectedOptimizePosition={selectedOptimizePosition}
            setSelectedOptimizePosition={setSelectedOptimizePosition}
            optimizePositions={optimizePositions}
            handleOptimizeSkills={handleOptimizeSkills}
            language={language}
            lang={lang}
          />

          <ScoutCommentsCard
            scoutComments={scoutComments}
            scoutCommentAuthor={scoutCommentAuthor}
            scoutCommentUpdatedAt={scoutCommentUpdatedAt}
            handleScoutCommentsChange={handleScoutCommentsChange}
            saveScoutComments={() => updateScoutComments(scoutComments)}
            isSavingScoutComment={isSavingScoutComment}
            hasUnsavedScoutComment={scoutComments !== savedScoutComments}
            candidates={candidates}
            selectedCandidate={selectedCandidate}
            handleCandidateSelect={handleCandidateSelect}
            updateCandidate={updateCandidate}
            canAddToList={!player.isCustom}
            lang={lang}
          />
          <SkillUpdateHistory history={skillUpdateHistory} lang={lang} />
        </Col>
      </Row>
    </>
  )
}

export default ScoutSkills
